import { Modal } from '@appkit4/react-components/modal';
import { Button } from '@appkit4/react-components/button';
import { Input } from '@appkit4/react-components/field';
import './SightlineModal.scss';
import { SightlineFilterResponse } from '../../../../api/generated/models';
import React, { useEffect } from 'react';

interface SightlineModalProps {
    visible: boolean;
    onClose: () => void;
    onSave: (data: {
        engagementIds: string[];
        categoryNames: string[];
        subCategoryNames: string[];
    }) => void;
    sightlineData?: SightlineFilterResponse;
    refetchData: () => void;
}

const SightlineModal: React.FC<SightlineModalProps> = ({
    visible,
    onClose,
    onSave,
    sightlineData,
    refetchData,
}) => {
    const [engagementIds, setEngagementIds] = React.useState<string[]>(
        sightlineData?.engagementIds || []
    );
    const [categoryNames, setCategoryNames] = React.useState<string[]>(
        sightlineData?.categoryNames || []
    );
    const [subCategoryNames, setSubCategoryNames] = React.useState<string[]>(
        sightlineData?.subCategoryNames || []
    );

    const [currentEngagementId, setCurrentEngagementId] = React.useState('');
    const [currentCategoryName, setCurrentCategoryName] = React.useState('');
    const [currentSubCategoryName, setCurrentSubCategoryName] =
        React.useState('');
    const [engagementIdError, setEngagementIdError] = React.useState<
        string | null
    >(null);

    useEffect(() => {
        if (visible) {
            refetchData();
        }
    }, [visible, refetchData]);

    useEffect(() => {
        if (sightlineData) {
            setEngagementIds(sightlineData.engagementIds || []);
            setCategoryNames(sightlineData.categoryNames || []);
            setSubCategoryNames(sightlineData.subCategoryNames || []);
        }
    }, [sightlineData]);

    const isValidGUID = (guid: string): boolean => {
        const guidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return guidRegex.test(guid);
    };

    const addEngagementId = () => {
        if (currentEngagementId && isValidGUID(currentEngagementId)) {
            setEngagementIds([...engagementIds, currentEngagementId]);
            setCurrentEngagementId('');
            setEngagementIdError(null);
        }
    };

    const addCategoryName = () => {
        if (currentCategoryName) {
            setCategoryNames([...categoryNames, currentCategoryName]);
            setCurrentCategoryName('');
        }
    };

    const addSubCategoryName = () => {
        if (currentSubCategoryName) {
            setSubCategoryNames([...subCategoryNames, currentSubCategoryName]);
            setCurrentSubCategoryName('');
        }
    };

    const handleSave = () => {
        if (engagementIds.length > 0) {
            onSave({ engagementIds, categoryNames, subCategoryNames });
            onClose();
        }
    };

    const removeItem = (
        itemType: 'engagementId' | 'categoryName' | 'subCategoryName',
        index: number
    ) => {
        if (itemType === 'engagementId') {
            setEngagementIds(engagementIds.filter((_, i) => i !== index));
        } else if (itemType === 'categoryName') {
            setCategoryNames(categoryNames.filter((_, i) => i !== index));
        } else if (itemType === 'subCategoryName') {
            setSubCategoryNames(subCategoryNames.filter((_, i) => i !== index));
        }
    };

    const handleEngagementIdChange = (value: string) => {
        setCurrentEngagementId(value);
        if (value && !isValidGUID(value)) {
            setEngagementIdError('Please enter a valid GUID.');
        } else {
            setEngagementIdError(null);
        }
    };

    return (
        <Modal
            visible={visible}
            title="Add Sightline Integration Details"
            ariaLabel="Add Sightline Integration Details Modal"
            className="sightline-modal"
            onCancel={onClose}
            modalStyle={{ width: '50rem' }}
            footerStyle={{
                paddingTop: '8px',
                marginTop: '-8px',
                minHeight: '64px',
                position: 'fixed',
            }}
            bodyStyle={{ minHeight: '300px' }}
            placement="center"
        >
            <div className="sightline-modal-content">
                <div className="input-section">
                    <label>Engagement IDs (required)</label>
                    <div className="input-group">
                        <Input
                            type="text"
                            value={currentEngagementId}
                            onChange={handleEngagementIdChange}
                            placeholder="Enter IDs"
                            style={{ width: '20rem' }}
                            error={engagementIdError ? true : false}
                            required
                        />
                        <div
                            onClick={
                                !currentEngagementId || !!engagementIdError
                                    ? undefined
                                    : addEngagementId
                            }
                            className={`add-group${
                                !currentEngagementId || !!engagementIdError
                                    ? ' disabled'
                                    : ''
                            }`}
                            style={{
                                pointerEvents:
                                    !currentEngagementId || !!engagementIdError
                                        ? 'none'
                                        : 'auto',
                                opacity:
                                    !currentEngagementId || !!engagementIdError
                                        ? 0.5
                                        : 1,
                            }}
                        >
                            <span className="Appkit4-icon icon-circle-plus-outline ap-font-20" />
                            <span className="add-label">Add ID</span>
                        </div>
                    </div>
                    {engagementIdError && (
                        <div
                            className="error-message"
                            style={{
                                color: 'red',
                                fontSize: '0.8rem',
                                marginTop: '0.2rem',
                            }}
                        >
                            {engagementIdError}
                        </div>
                    )}
                    <div className="items">
                        {engagementIds.map((id, index) => (
                            <span key={index}>
                                {id}{' '}
                                <span
                                    className="Appkit4-icon icon-delete-outline"
                                    onClick={() =>
                                        removeItem('engagementId', index)
                                    }
                                    style={{ cursor: 'pointer' }}
                                ></span>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="input-section">
                    <label>Category Names</label>
                    <div className="input-group">
                        <Input
                            type="text"
                            value={currentCategoryName}
                            onChange={(value: string) =>
                                setCurrentCategoryName(value)
                            }
                            placeholder="Enter Category"
                            style={{ width: '20rem' }}
                        />
                        <div className="add-group" onClick={addCategoryName}>
                            <span className="Appkit4-icon icon-circle-plus-outline ap-font-20" />
                            <span className="add-label">Add Category</span>
                        </div>
                    </div>
                    <div className="items">
                        {categoryNames.map((name, index) => (
                            <span key={index}>
                                {name}{' '}
                                <span
                                    className="Appkit4-icon icon-delete-outline"
                                    onClick={() =>
                                        removeItem('categoryName', index)
                                    }
                                    style={{ cursor: 'pointer' }}
                                ></span>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="input-section">
                    <label>Subcategory Names</label>
                    <div className="input-group">
                        <Input
                            type="text"
                            value={currentSubCategoryName}
                            onChange={(value: string) =>
                                setCurrentSubCategoryName(value)
                            }
                            placeholder="Enter SubCategory"
                            style={{ width: '20rem' }}
                        />
                        <div className="add-group" onClick={addSubCategoryName}>
                            <span className="Appkit4-icon icon-circle-plus-outline ap-font-20" />
                            <span className="add-label">Add Subcategory</span>
                        </div>
                    </div>
                    <div className="items">
                        {subCategoryNames.map((subName, index) => (
                            <span key={index}>
                                {subName}{' '}
                                <span
                                    className="Appkit4-icon icon-delete-outline"
                                    onClick={() =>
                                        removeItem('subCategoryName', index)
                                    }
                                    style={{ cursor: 'pointer' }}
                                ></span>
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            <div className="modal-footer">
                <Button kind="tertiary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    kind="primary"
                    onClick={handleSave}
                    disabled={engagementIds.length === 0}
                >
                    Save
                </Button>
            </div>
        </Modal>
    );
};

export default SightlineModal;
